import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { FilehandlerService } from '@app/core/services/file/filehandler.service';
import { SessionService } from '@app/core/services/session/session.service';
import { SnackbarService } from '@app/core/services/snackbar/snackbar.service';
import { LanguageService } from '@app/core/services/translate/language.service';
import { FileInstance } from '@app/features/device/model/device-imported-exported-data-interface';
import { Notification } from '@app/features/notification/model/notification.model';
import { NotificationCommandService } from '@app/features/notification/services/notification-command.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'vfs-notification-viewer-item',
  templateUrl: './notification-viewer-item.component.html',
  styleUrls: ['./notification-viewer-item.component.scss']
})
export class NotificationViewerItemComponent implements OnInit, AfterViewInit {
  @Input() notification: Notification;
  @Input() multiselectActive = false;
  @Input() selection: EntitySelectionModel<Notification>;

  disableAnimation = true;
  disablePanelHeader = false;
  disableDownloadButton = false;

  constructor(
    protected notificationCommandService: NotificationCommandService,
    protected languageService: LanguageService,
    protected translateService: TranslateService,
    protected fileHandlerService: FilehandlerService,
    protected snackbarService: SnackbarService,
    protected sessionService: SessionService,
    protected router: Router
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    // FIXME:
    setTimeout(() => (this.disableAnimation = false));
  }

  public markAsFlagged() {
    this.disablePanelHeader = true;
    this.notificationCommandService.markAsFlagged(this.notification);
  }

  public delete() {
    this.disablePanelHeader = true;
    this.notificationCommandService.deleteById(this.notification.id);
  }

  public getCategoryTx(): string {
    return this.notification.category === 'FLAGGED'
      ? this.translateService.instant('core.notifications.flagged')
      : this.notification.category === 'ERROR'
      ? this.translateService.instant('core.notifications.error')
      : '';
  }

  public onNotificationOpen() {
    if (this.notification.unread) {
      this.markAsRead(true);
    }
  }

  public markAsRead(read: boolean, panel?: MatExpansionPanel) {
    this.notificationCommandService.markAsRead(this.notification, read, panel);
  }

  public selected() {
    this.selection.toggle(this.notification);
  }

  public isSelected(): boolean {
    return this.selection.isSelected(this.notification);
  }

  onDownloadButtonClick(): void {
    // Safety check, this should never happen
    if (!this.notification.downloadFileId) {
      return;
    }

    combineLatest(
      this.fileHandlerService.downloadFile(this.notification.downloadFileId),
      this.fileHandlerService.getFileInfo(this.notification.downloadFileId)
    )
      .pipe(
        catchError(error => {
          this.disableDownloadButton = true;
          return throwError(error);
        })
      )
      .subscribe(
        (result: [Blob, FileInstance]) => this.saveDownloadedFile(result[0], result[1]),
        error => {
          // FIXME: find proper translation
          this.snackbarService.openSnackBar(
            this.translateService.instant('core.notifications.fileNoLongerAvailable'),
            'warning'
          );
        }
      );
  }

  onRoutingLinkClick(): void {
    this.router.navigateByUrl(this.notification.routingLink);
  }

  protected saveDownloadedFile(blob: Blob, info: FileInstance): void {
    const newBlob = new Blob([blob], { type: info.type });
    const anchor = document.createElement('a');
    anchor.download = info.name + '.' + info.extension;
    anchor.href = window.URL.createObjectURL(newBlob);
    anchor.click();
    anchor.remove();
  }

  getLocale(): string {
    return this.sessionService.getLocale();
  }
}
